'use memo';

import {
  FormCondition,
  RenderedSpecialCondition,
  SpecialConditionData,
  useConstructConditionClauseText,
} from '@urbanx/agx.campaigns.client';
import { AgxEmptyState, AgxSpacer } from '@urbanx/agx.ui-components2';
import { TaskModalPageProps } from 'pages/allTasks/TaskModal/TaskModalPageProps';
import { useEffect, useRef, useState } from 'react';
import { AddSpecialConditionButton } from '../contract-information/modals/specialConditions/AddSpecialConditionButton';
import SpecialConditionCollapsiblePanel from '../contract-information/panels/SpecialConditionCollapsiblePanel';
import {
  isActionableSpecialCondition,
  withUpdatedSpecialConditionDueDate,
} from '../contract-information/specialConditionDueDate';

interface ContractComplianceSpecialConditionsProps extends TaskModalPageProps {
  specialConditions?: SpecialConditionData[];
  onSpecialConditionsUpdated: (
    specialConditions: SpecialConditionData[]
  ) => boolean | Promise<boolean>;
  addButtonVariant?: 'primary' | 'hollow' | 'dark';
}

const EMPTY_SPECIAL_CONDITIONS: SpecialConditionData[] = [];

const ContractComplianceSpecialConditions = ({
  campaign,
  addButtonVariant,
  onSpecialConditionsUpdated,
  registerBeforeTaskModalSave,
  specialConditions = EMPTY_SPECIAL_CONDITIONS,
  task,
}: ContractComplianceSpecialConditionsProps) => {
  const [renderedSpecialConditions, setRenderedSpecialConditions] = useState<
    RenderedSpecialCondition[] | undefined
  >(undefined);
  const renderRequestVersion = useRef(0);

  const contractId = task.contractId ?? '';
  const campaignId = campaign.campaignId ?? '';

  const { constructConditionClauseText } = useConstructConditionClauseText({
    onSuccess: () => {},
    onError: () => {},
  });

  useEffect(() => {
    if (!campaignId || !contractId) return;

    const requestVersion = ++renderRequestVersion.current;

    if (specialConditions.length === 0) {
      setRenderedSpecialConditions((prevRendered) =>
        prevRendered?.length === 0 ? prevRendered : []
      );
      return;
    }

    void constructConditionClauseText({
      campaignId,
      contractId,
      model: specialConditions,
    })
      .then((conditionTextBlocks) => {
        if (requestVersion !== renderRequestVersion.current) return;

        setRenderedSpecialConditions(conditionTextBlocks);
      })
      .catch(() => {
        if (requestVersion !== renderRequestVersion.current) return;

        setRenderedSpecialConditions(undefined);
      });
  }, [campaignId, constructConditionClauseText, contractId, specialConditions]);

  const onAddCustomCondition = async (customSpecialCondition: SpecialConditionData) => {
    return await onSpecialConditionsUpdated([...specialConditions, customSpecialCondition]);
  };

  const onDueDateUpdated = async (
    conditionId: string,
    clauseId: string,
    newDueDateCondition: FormCondition | undefined,
    newDueDate: string | undefined
  ) => {
    const wasUpdated = await onSpecialConditionsUpdated(
      specialConditions.map((condition) => {
        if (condition.id !== conditionId) return condition;

        return withUpdatedSpecialConditionDueDate(condition, clauseId, newDueDateCondition);
      })
    );
    if (!wasUpdated) return;

    setRenderedSpecialConditions((prevRendered) =>
      prevRendered?.map((renderedCondition) => {
        if (renderedCondition.id !== conditionId) return renderedCondition;

        return {
          ...renderedCondition,
          directDueDate: conditionId === clauseId ? newDueDate : renderedCondition.directDueDate,
          clauses: renderedCondition.clauses?.map((clause) => {
            if (clause.clauseId !== clauseId) return clause;

            return {
              ...clause,
              dueDate: newDueDate,
            };
          }),
        };
      })
    );
  };

  const onConditionDeleted = async (conditionId: string) => {
    const wasUpdated = await onSpecialConditionsUpdated(
      specialConditions.filter((condition) => condition.id !== conditionId)
    );
    if (!wasUpdated) return;

    setRenderedSpecialConditions((conditions) =>
      conditions?.filter((condition) => condition.id !== conditionId)
    );
  };

  const visibleRenderedSpecialConditions = renderedSpecialConditions?.filter(
    isActionableSpecialCondition
  );

  const canShowEmptyState = visibleRenderedSpecialConditions?.length === 0;

  return (
    <AgxSpacer direction="vertical" fill gap="spacing-xl">
      {canShowEmptyState && (
        <AgxEmptyState variant="minimal" label="No special conditions added." />
      )}
      <AgxSpacer direction="vertical" fill gap="spacing-xl" padding={{ bottom: 'md' }}>
        {visibleRenderedSpecialConditions?.map((renderedCondition) => (
          <SpecialConditionCollapsiblePanel
            key={renderedCondition.id ?? renderedCondition.conditionType}
            renderedSpecialCondition={renderedCondition}
            specialCondition={specialConditions.find(
              (condition) => condition.id === renderedCondition.id
            )}
            onDeleteCondition={onConditionDeleted}
            skipDeleteConfirmation
            campaignId={campaignId}
            contractId={contractId}
            onDueDateUpdated={onDueDateUpdated}
          />
        ))}

        <AddSpecialConditionButton
          id="contract-compliance-add-special-condition-button"
          label="Add Special Conditions"
          existingConditions={specialConditions}
          renderedConditions={renderedSpecialConditions}
          onAddSpecialCondition={onAddCustomCondition}
          campaignId={campaignId}
          buttonVariant={addButtonVariant}
          mode="inline"
          registerBeforeSave={registerBeforeTaskModalSave}
        />
      </AgxSpacer>
    </AgxSpacer>
  );
};

export default ContractComplianceSpecialConditions;
